//@flow
import * as React from 'react'
import { useState } from 'react'
import Debug from 'debug'
import semver from 'semver'
import getConfig from 'next/config'

import { Topbar, Sidebar } from '../'
import { useKeycloak } from './hooks/useKeycloak'
import { useLegacyAuth } from './hooks/useLegacyAuth'
import { Button, Loader, Modal as ModalContainer, NotAllowed } from 'components/common'
import { checkIframe, saveToLocalStorage } from '~/core/utils/functions'
import { getAuthInfo, pbxLogin, pbxLogout } from '~/core/api/api'
import { useGlobalContext, useGlobalDispatch } from '~/core/context'
import { useI18n } from '~/core/i18n'
import { decodeAuthToken, isEmptyArray } from '~/core/utils/functions'

import type { Page } from '../types.flow'

const { KEYCLOAK_URL, KEYCLOAK_REALM, KEYCLOAK_CLIENT, PBX_VERSION_OVERRIDE } = getConfig().publicRuntimeConfig
const debug = Debug('auth')

class AuthError extends Error {
  type: string

  constructor(type, message) {
    super(message)
    this.type = type
  }
}

type AuthLayoutProps = {
  children: React.Node,
  sites: {
    [key: string]: Page
  },
  router: {
    query: {
      auth?: string
    },
    asPath: string
  }
}

const getUserAuthInfo = async (isIframe: boolean) => {
  const { email, roles, anvilId, ...pbxInfo } = await getAuthInfo()
  const { permissions } = decodeAuthToken()

  if (semver.valid(PBX_VERSION_OVERRIDE)) {
    pbxInfo.version = PBX_VERSION_OVERRIDE
  }

  if (!isIframe) {
    debug('checking version', { satisfy: '>=4.6.0', version: pbxInfo.version })
    if (!semver.satisfies(pbxInfo.version, '>=4.6.0')) {
      throw new AuthError('version', 'PBX version does not satisfy condition >=4.6.0')
    }
  }

  debug('checking permissions', { permissions })
  if (!permissions || !Array.isArray(permissions) || isEmptyArray(permissions)) {
    throw new AuthError('permissions', `User's access token does not have any configured permissions`)
  }

  return {
    auth: {
      anvilId,
      email,
      roles
    },
    pbx: pbxInfo,
    permissions
  }
}

const AuthLayout = ({ sites, router, children }: AuthLayoutProps) => {
  const isIframe = checkIframe()
  const isLegacyAuthUsed = isIframe

  const [msg] = useI18n()
  const { modal } = useGlobalContext()
  const globalDispatch = useGlobalDispatch()
  const [{ isRendering, error }, setAuthState] = useState({
    isRendering: true,
    error: null
  })
  const keycloak = useKeycloak({
    skip: isLegacyAuthUsed,
    initOptions: {
      onLoad: 'login-required'
    },
    config: {
      url: KEYCLOAK_URL,
      realm: KEYCLOAK_REALM,
      clientId: KEYCLOAK_CLIENT
    },
    onAuthSuccess: async keycloak => {
      debug('auth success')
      saveToLocalStorage('anvilToken', keycloak.token)

      const { auth, pbx, permissions } = await getUserAuthInfo(isIframe)

      if (!isIframe) {
        await pbxLogin()
      }

      globalDispatch({
        type: 'pbx/update',
        payload: {
          ...pbx
        }
      })

      globalDispatch({
        type: 'permissions/set',
        payload: permissions
      })

      globalDispatch({
        type: 'auth/update',
        payload: auth
      })

      setAuthState({
        isRendering: false,
        error: null
      })
    },
    onAuthRefreshSuccess: async keycloak => {
      debug('auth refresh success')
      saveToLocalStorage('anvilToken', keycloak.token)

      if (!isIframe) {
        await pbxLogin()
      }
    },
    onAuthRefreshError: error => {
      console.error('[Auth] Auth refresh error, logging out...', error)
      handleLogout()
    },
    onAuthError: error => {
      localStorage.removeItem('anvilToken')
      setAuthState({
        isRendering: false,
        error: error
      })
    }
  })

  // Will be deleted after implementing Keycloack into communicator
  // Used only for external link in communicator
  useLegacyAuth({
    skip: !isLegacyAuthUsed,
    onAuthSuccess: async () => {
      debug('user authorized through legacy auth', { isIframe })

      const { auth, pbx, permissions } = await getUserAuthInfo(isIframe)

      if (!isIframe) {
        await pbxLogin()
      }

      globalDispatch({
        type: 'pbx/update',
        payload: {
          ...pbx
        }
      })

      globalDispatch({
        type: 'permissions/set',
        payload: permissions
      })

      globalDispatch({
        type: 'auth/update',
        payload: auth
      })

      setAuthState({
        isRendering: false,
        error: null
      })
    },
    onAuthError: error => {
      setAuthState({
        isRendering: false,
        error: error
      })
    }
  })

  const url = router.asPath
  const pages: Page[] = (Object.values(sites): any)
  const actualPage = pages.find(({ path }) => url.includes(path)) || null

  const handleModalClose = () =>
    globalDispatch({
      type: 'modal/close'
    })

  const handleLogout = async () => {
    try {
      debug('logging out from keycloak')
      keycloak.logout({
        redirectUri: window.location.origin
      })

      await pbxLogout()
    } catch (error) {
      console.error('[AuthLayout] Unknown error occured', error)
    }
  }

  if (error) {
    const ToolsComponent = !isLegacyAuthUsed ? (
      <Button iconPosition="left" xIcon="chevronleft" xStyle="flat" onClick={handleLogout}>
        Zpět na přihlášení
      </Button>
    ) : null

    if (error instanceof AuthError) {
      return (
        <NotAllowed
          TextComponent={msg(
            error.type === 'version' ? 'common.errors.versionNotSatisfies' : 'common.errors.permissionsNotSatisfies'
          )}
          Tools={ToolsComponent}
        />
      )
    } else {
      return <NotAllowed info={error.message} TextComponent={msg('common.errors.unknown')} Tools={ToolsComponent} />
    }
  }

  return !isRendering ? (
    isIframe ? (
      <section className="callcntr_content" id="page-wrapper" style={{ height: '100%', padding: 0 }}>
        <ModalContainer
          isCloseDisabled={modal.isCloseDisabled}
          isOpen={!!modal.content}
          title={modal.title}
          onClose={handleModalClose}
        >
          {modal.content}
        </ModalContainer>
        {children}
      </section>
    ) : (
      <div style={{ height: '100%' }}>
        <ModalContainer
          isCloseDisabled={modal.isCloseDisabled}
          isOpen={!!modal.content}
          title={modal.title}
          onClose={handleModalClose}
        >
          {modal.content}
        </ModalContainer>
        <Sidebar pages={pages} url={url} onLogout={handleLogout} />
        <article className="app-window callcntr">
          <Topbar actualPage={actualPage} actualPath={url} />
          <section className="callcntr_content" id="page-wrapper" style={{ padding: 0 }}>
            {children}
          </section>
        </article>
      </div>
    )
  ) : (
    <Loader />
  )
}

export default AuthLayout
